/**
 * dockview panel + tab + header-action components and the registries wired into
 * `DockviewReact` (架构方案 §5.7): `DOCK_COMPONENTS` (content), `TAB_COMPONENTS`
 * (tab headers) and `AuxiliaryBarActions` (`rightHeaderActionsComponent`).
 */
import type {
  LayoutArea,
  TabHostHeaderActionsProps,
  TabHostTabProps,
} from '@hibro/shared';
import {
  buttonVariants,
  cn,
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
  Icon,
} from '@hibro/ui-client';
import type {
  IDockviewGroupDragGhostProps,
  IDockviewHeaderActionsProps,
  IDockviewPanelHeaderProps,
  IDockviewPanelProps,
} from 'dockview-react';
import {
  Fragment,
  type FunctionComponent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useViewEventBus } from '../../event-bus/event-bus';
import { useTranslator } from '../../i18n/i18n';
import { isDetachedInstance } from '../../surfaces/detached-views';
import { HostVisibilityContext } from '../../surfaces/host-visibility';
import { DetachedPlaceholder } from '../detached-placeholder';
import { resolveViewCachePolicy } from '../editor/editor-cache';
import { useWorkbenchLayout } from '../layout-context';
import { AreaContentContext } from './area-content-context';
import { AREA_SPECS } from './area-specs';
import { EditorSingletonTitleContext } from './editor-singleton-title-context';
import { PanelVisibilityProvider } from './panel-visibility';
import { selectSurfaceTabHost } from './select-surface-tab-host';
import { suppressDockviewTabActivation } from './suppress-tab-activation';
import { SurfaceHostContext } from './surface-host-context';
import { useTabContentMounted } from './tab-content-cache';
import { TabDetachContext } from './tab-detach-context';
import { resolveTabDetachActions } from './tab-detach-menu';
import { TabHostRuntimeContext } from './tab-host-context';

interface AreaPanelParams {
  area: LayoutArea;
}

/** Generic dockview content renderer — renders whatever the area content is. */
function AreaPanel({
  api,
  params,
}: IDockviewPanelProps<AreaPanelParams>): ReactNode {
  const renderArea = useContext(AreaContentContext);
  return (
    <PanelVisibilityProvider api={api}>
      {renderArea(params.area)}
    </PanelVisibilityProvider>
  );
}

/** Custom tab header for the bottom `panel` area (no close button). */
function PanelTab(): ReactNode {
  const { t } = useTranslator();
  const title = t('workbench.area.panel');
  return <div title={title}>{title}</div>;
}

/**
 * Editor singleton tab: no close (closing the anchor destroys the editor
 * area; `reconcileEnabledAreas` will not recreate it). Visible only because
 * sibling instance tabs lift the strip — see `dockview-theme.css`. It must not
 * carry `.app-surface-tab`, or that strip would stay up with no documents open.
 */
function EditorSingletonTab({
  api,
}: IDockviewPanelHeaderProps<AreaPanelParams>): ReactNode {
  const title = useContext(EditorSingletonTitleContext);
  useEffect(() => {
    if (title) {
      api.setTitle(title);
    }
  }, [api, title]);
  return (
    <div
      className="app-editor-singleton-tab flex items-center gap-2 h-full"
      title={title}
    >
      <span className="max-w-40 truncate">{title}</span>
    </div>
  );
}

/**
 * Tab header for a tabbed area's structural *anchor* panel (§5.7). The
 * anchor keeps the group (and thus the area's visibility / sizing machinery)
 * alive even when it holds no view instances.
 *
 * - `auxiliaryBar`: empty marker; CSS collapses the strip `.dv-tab` to 0
 *   width and pins it with `sticky` / `left: 0` (`:has(.app-anchor-tab)`, not
 *   `display: none`, so Dockview overflow math does not count it) and hides
 *   overflow clones separately, so only module-contributed view tabs show.
 * - `editor`: a real, unclosable singleton tab titled from the active
 *   module. The strip itself stays hidden until an instance tab (marked
 *   `.app-surface-tab`) is present, matching the pre-tabbed empty look.
 *
 * `data-app-area-anchor` marks the tab as the *area* rather than one of the
 * documents open in it, which is the distinction the drag veto in
 * `useDockviewManager` needs: a locked area's anchor tab must not start a drag,
 * while its instance tabs stay draggable. Stamped on a `display: contents`
 * wrapper so it lands in exactly one place without altering either branch's
 * layout — a branch that lost the marker would silently become draggable.
 */
function AreaAnchorTab(
  props: IDockviewPanelHeaderProps<AreaPanelParams>,
): ReactNode {
  return (
    <div className="contents" data-app-area-anchor={props.params.area}>
      {props.params.area === 'editor' ? (
        <EditorSingletonTab {...props} />
      ) : (
        <span className="app-anchor-tab" aria-hidden="true" />
      )}
    </div>
  );
}

/**
 * Workbench-owned group-drag ghost. Excludes every area's structural anchor so
 * its title/count matches the tabs visible to the user — by area id rather than
 * the auxiliaryBar's alone, since `editor` is a tabbed host too now (§5.7).
 * The editor singleton is user-visible once instance tabs exist, so count it
 * then; auxiliaryBar's anchor stays hidden always.
 */
const CustomGroupDragGhost: FunctionComponent<IDockviewGroupDragGhostProps> = ({
  group,
}) => {
  const hasInstanceTabs = group.panels.some(
    (panel) => !(panel.id in AREA_SPECS),
  );
  const visiblePanels = group.panels.filter((panel) => {
    if (panel.id === 'editor') {
      return hasInstanceTabs;
    }
    return !(panel.id in AREA_SPECS);
  });
  if (visiblePanels.length === 1) {
    return <div>{visiblePanels[0].api.title}</div>;
  }
  return <div>Multiple Panels（{visiblePanels.length}）</div>;
};

/** Params carried by a dynamically-added surface (view-instance) tab panel. */
interface SurfaceTabParams {
  viewId: string;
  instanceKey?: string;
  title: string;
}

/**
 * Content renderer for a native dockview tab that hosts one view instance
 * (§5.7 视图多实例). Resolves the «render a SurfaceView» closure from
 * {@link SurfaceHostContext} (dockview only forwards serializable params, so the
 * heavy deps come through Context). Keyed by `viewId` + `instanceKey`, this is
 * how the `auxiliaryBar` area hosts e.g. multiple AI chat sessions as tabs.
 */
function SurfaceTabPanel({
  api,
  params,
}: IDockviewPanelProps<SurfaceTabParams>): ReactNode {
  return (
    <PanelVisibilityProvider api={api}>
      <SurfaceTabContent panelId={api.id} params={params} />
    </PanelVisibilityProvider>
  );
}

/**
 * The tab's content, mounted lazily and kept alive on a budget (§5.7 视图缓存).
 *
 * A tab exists from the moment the layout is restored, but its content waits
 * for the tab to actually be revealed — a reload must not render (and a
 * background open must not fetch) documents nobody has asked for yet. After the
 * first reveal the tree stays mounted while `TabContentCache` keeps it, so
 * switching back is instant; eviction unmounts it, and the next click pays for a
 * fresh mount just like the first one did.
 *
 * Reads the panel's on-screen flag from the projection its own panel component
 * just installed, so there is exactly one visibility signal in play.
 */
function SurfaceTabContent({
  panelId,
  params,
}: {
  panelId: string;
  params: SurfaceTabParams;
}): ReactNode {
  const renderSurface = useContext(SurfaceHostContext);
  const runtime = useContext(TabHostRuntimeContext);
  const onScreen = useContext(HostVisibilityContext);
  const detach = useContext(TabDetachContext);
  const { title } = useTranslator();
  const view = runtime?.views.find(
    (candidate) => candidate.id === params.viewId,
  );
  const keepAlive =
    view !== undefined && resolveViewCachePolicy(view) === 'keep-alive';
  // A migrated instance renders in its own window (§5.8), so its inline copy
  // stays unmounted and off the keep-alive budget. Leaving it up would render
  // the view twice and — for a native-content host — keep measuring bounds for
  // a WebContentsView that has already moved to the other window.
  const detached = isDetachedInstance(
    detach.detachedSurfaces,
    params.viewId,
    params.instanceKey,
  );
  const mounted = useTabContentMounted(
    panelId,
    onScreen && !detached,
    keepAlive,
  );
  if (detached) {
    const { onFocusDetached, onAttachView } = detach;
    return (
      <DetachedPlaceholder
        title={
          view ? title(view.moduleId, view.title ?? view.id) : params.title
        }
        onFocusDetached={
          onFocusDetached
            ? () => onFocusDetached(params.viewId, params.instanceKey)
            : undefined
        }
        onAttachView={
          onAttachView
            ? () => onAttachView(params.viewId, params.instanceKey)
            : undefined
        }
      />
    );
  }
  return mounted ? renderSurface(params.viewId, params.instanceKey) : null;
}

/** Generic fallback tab chrome: title plus close. Modules may replace it. */
function LegacySurfaceTab({
  api,
  params,
}: IDockviewPanelHeaderProps<SurfaceTabParams>): ReactNode {
  const { t } = useTranslator();
  const closeTab = t('workbench.editor.closeTab');
  const [title, setTitle] = useState(api.title ?? params.title);

  useEffect(() => {
    const sub = api.onDidTitleChange(() => setTitle(api.title ?? params.title));
    return () => sub.dispose();
  }, [api, params.title]);

  return (
    <div
      className="app-surface-tab flex items-center gap-2 h-full"
      title={title}
    >
      <span className="max-w-40 truncate">{title}</span>
      <span
        role="button"
        tabIndex={-1}
        aria-label={closeTab}
        title={closeTab}
        onPointerDown={(e) => suppressDockviewTabActivation(e)}
        onMouseDown={(e) => suppressDockviewTabActivation(e)}
        onClick={(e) => {
          suppressDockviewTabActivation(e);
          void api.close();
        }}
        className={cn(
          'flex size-4 shrink-0 items-center justify-center rounded',
          'text-muted-foreground opacity-70 hover:bg-accent hover:opacity-100',
        )}
      >
        <Icon spec={{ type: 'builtin', id: 'close' }} size={16} />
      </span>
    </div>
  );
}

/**
 * Right-click chrome shared by every view-instance tab (§5.8).
 *
 * The platform's generic entry for popping one tab out: a module with its own
 * toolbar (the browser) may offer a button too, but a plain document tab has
 * nowhere else to put it. Wraps whatever tab chrome is in use so a module's
 * custom tab gets the same menu as the default one.
 */
function SurfaceTabMenu({
  viewId,
  instanceKey,
  moduleId,
  detachable,
  children,
}: {
  viewId: string;
  instanceKey?: string;
  moduleId?: string;
  detachable?: boolean;
  children: ReactNode;
}): ReactNode {
  const { t } = useTranslator();
  const detach = useContext(TabDetachContext);
  const actions = resolveTabDetachActions({
    detachable,
    detached: isDetachedInstance(detach.detachedSurfaces, viewId, instanceKey),
    handlers: {
      detach: detach.onDetachView !== undefined && moduleId !== undefined,
      focusDetached: detach.onFocusDetached !== undefined,
      attach: detach.onAttachView !== undefined,
    },
  });
  if (actions.length === 0) {
    return children;
  }
  return (
    <ContextMenu>
      <ContextMenuTrigger render={<div className="h-full" />}>
        {children}
      </ContextMenuTrigger>
      <ContextMenuContent>
        {actions.map((action, index) => (
          <Fragment key={action}>
            {index > 0 ? <ContextMenuSeparator /> : null}
            <ContextMenuItem
              onClick={() => {
                if (action === 'detach') {
                  detach.onDetachView?.(
                    viewId,
                    moduleId ?? viewId,
                    instanceKey,
                  );
                  return;
                }
                if (action === 'focusDetached') {
                  detach.onFocusDetached?.(viewId, instanceKey);
                  return;
                }
                detach.onAttachView?.(viewId, instanceKey);
              }}
            >
              {t(
                action === 'detach'
                  ? 'surface.detach.title'
                  : action === 'focusDetached'
                    ? 'workbench.editor.focusWindow'
                    : 'workbench.editor.attachBack',
              )}
            </ContextMenuItem>
          </Fragment>
        ))}
      </ContextMenuContent>
    </ContextMenu>
  );
}

function SurfaceTab(
  props: IDockviewPanelHeaderProps<SurfaceTabParams>,
): ReactNode {
  const runtime = useContext(TabHostRuntimeContext);
  const subscribeEvent = useViewEventBus();
  const translator = useTranslator();
  const [, setVersion] = useState(0);
  const view = runtime?.views.find(
    (candidate) => candidate.id === props.params.viewId,
  );
  const surfaceHost =
    runtime && view
      ? selectSurfaceTabHost(view.area, {
          auxiliaryBar: runtime.host,
          editor: runtime.editorHost,
        })
      : undefined;

  useEffect(() => {
    if (!surfaceHost) {
      return;
    }
    return surfaceHost.subscribe(() => setVersion((version) => version + 1));
  }, [surfaceHost]);

  useEffect(() => {
    if (!runtime || !view) {
      return;
    }
    let active = true;
    void runtime.registry.activateForModule(view.moduleId).then(() => {
      if (active) {
        setVersion((version) => version + 1);
      }
    });
    return () => {
      active = false;
    };
  }, [runtime, view]);

  const t = useCallback<TabHostTabProps['t']>(
    (key, args) => (view ? translator.t(`${view.moduleId}:${key}`, args) : key),
    [translator, view],
  );
  const onEvent = useCallback<TabHostTabProps['onEvent']>(
    (event, handler) => subscribeEvent(event, handler),
    [subscribeEvent],
  );

  const tab = surfaceHost
    ?.getTabs()
    .find(
      (candidate) =>
        candidate.viewId === props.params.viewId &&
        candidate.instanceKey === props.params.instanceKey,
    );
  const CustomTab = view
    ? (runtime?.registry.getViewTabChrome(view.id)?.tab as
        | FunctionComponent<TabHostTabProps>
        | undefined)
    : undefined;
  return (
    <SurfaceTabMenu
      viewId={props.params.viewId}
      instanceKey={props.params.instanceKey}
      moduleId={view?.moduleId}
      detachable={view?.detachable}
    >
      {runtime && view && tab && CustomTab && surfaceHost ? (
        <CustomTab
          moduleId={view.moduleId}
          viewId={view.id}
          host={surfaceHost}
          tab={tab}
          executeCommand={runtime.executeCommand}
          onEvent={onEvent}
          t={t}
        />
      ) : (
        <LegacySurfaceTab {...props} />
      )}
    </SurfaceTabMenu>
  );
}

/** Generic auxiliaryBar fallback action: hide the area. */
function LegacyAuxiliaryBarActions({
  group,
  containerApi,
}: IDockviewHeaderActionsProps): ReactNode {
  const { t } = useTranslator();
  const { toggle } = useWorkbenchLayout();
  const isAuxGroup = containerApi.getPanel('auxiliaryBar')?.group === group;
  if (!isAuxGroup) {
    return null;
  }
  const iconButton = cn(
    buttonVariants({ variant: 'ghost', size: 'icon' }),
    'size-7 text-muted-foreground',
  );
  return (
    <div className="app-aux-actions flex items-center gap-0.5 pe-1">
      <button
        type="button"
        title={t('workbench.action.closeAuxiliaryBar')}
        onClick={() => toggle('auxiliaryBar')}
        className={iconButton}
      >
        <Icon spec={{ type: 'builtin', id: 'close' }} size={16} />
      </button>
    </div>
  );
}

function AuxiliaryBarActions(props: IDockviewHeaderActionsProps): ReactNode {
  const runtime = useContext(TabHostRuntimeContext);
  const subscribeEvent = useViewEventBus();
  const translator = useTranslator();
  const [, setVersion] = useState(0);
  const isHostGroup =
    runtime &&
    props.containerApi.getPanel(runtime.host.area)?.group === props.group;
  const tabs = runtime?.host.getTabs() ?? [];
  const activeTab = tabs.find((tab) => tab.active) ?? tabs[0];
  const candidateViews =
    runtime?.views.filter((view) => view.area === runtime.host.area) ?? [];
  const view =
    candidateViews.find((candidate) => candidate.id === activeTab?.viewId) ??
    (candidateViews.length === 1 ? candidateViews[0] : undefined);

  useEffect(() => {
    if (!runtime) {
      return;
    }
    return runtime.host.subscribe(() => setVersion((version) => version + 1));
  }, [runtime]);

  useEffect(() => {
    if (!runtime || !view) {
      return;
    }
    let active = true;
    void runtime.registry.activateForModule(view.moduleId).then(() => {
      if (active) {
        setVersion((version) => version + 1);
      }
    });
    return () => {
      active = false;
    };
  }, [runtime, view]);

  const t = useCallback<TabHostHeaderActionsProps['t']>(
    (key, args) => (view ? translator.t(`${view.moduleId}:${key}`, args) : key),
    [translator, view],
  );
  const onEvent = useCallback<TabHostHeaderActionsProps['onEvent']>(
    (event, handler) => subscribeEvent(event, handler),
    [subscribeEvent],
  );
  const CustomActions = view
    ? (runtime?.registry.getViewTabChrome(view.id)?.headerActions as
        | FunctionComponent<TabHostHeaderActionsProps>
        | undefined)
    : undefined;

  if (runtime && isHostGroup && view && CustomActions) {
    return (
      <CustomActions
        moduleId={view.moduleId}
        viewId={view.id}
        host={runtime.host}
        activeTab={activeTab}
        executeCommand={runtime.executeCommand}
        onEvent={onEvent}
        t={t}
      />
    );
  }
  return <LegacyAuxiliaryBarActions {...props} />;
}

/** Content-component registry passed to DockviewReact. */
export const DOCK_COMPONENTS = {
  area: AreaPanel,
  surfaceTab: SurfaceTabPanel,
};

/**
 * Tab-component registry: `panel` header, the area-anchor tab (collapsed on
 * auxiliaryBar; editor singleton when instance tabs exist), and the
 * `surfaceTab` view-instance header (inline rename + themed close, §5.7).
 */
export const TAB_COMPONENTS = {
  panel: PanelTab,
  areaAnchor: AreaAnchorTab,
  surfaceTab: SurfaceTab,
};

/** Params for a `surfaceTab` panel (view-instance tab); used by `openAuxTab`. */
export type { SurfaceTabParams };

/** Right header actions for auxiliaryBar: add, history/delete, and hide-area. */
export { AuxiliaryBarActions, CustomGroupDragGhost };
